import { match, P } from 'ts-pattern';

import { MetricConfig } from '@/types/evals';

import { SectionBlock } from '../../_components/SectionBlock';
import { KeyValueTable } from './KeyValueTable';
import styles from './MetricConfigBlock.module.scss';

function ProducesMetricsBlock( { metrics }: { metrics: string[] } ) {
  return (
    <SectionBlock title="Produces Metrics" data-testid="produces-metrics-block">
      <p className={ styles.body }>{ metrics.length ? metrics.join( ', ' ) : '—' }</p>
    </SectionBlock>
  );
}

/**
 * Renders the type-specific configuration for a metric version. LLM-as-a-Judge
 * metrics show the grading prompt, prompt parameters, and produced metrics; API
 * classifiers show their endpoint details. Mirrors the source ConfigBlock pattern.
 */
export function MetricConfigBlock( { config }: { config: MetricConfig } ) {
  return match( config.configuration )
    .with( { url: P.string }, ( configuration ) => {
      const headerEntries: [ string, string ][] = ( configuration.headers ?? [] ).map( h => [ h.header, h.value ] );
      return (
        <>
          <SectionBlock title="API URL" data-testid="api-url-block">
            <p className={ styles.body }>{ configuration.url }</p>
          </SectionBlock>
          { headerEntries.length > 0 && (
            <SectionBlock title="API Headers" data-testid="api-headers-block">
              <KeyValueTable entries={ headerEntries } keyLabel="Name" valueLabel="Value" data-testid="api-headers-table" />
            </SectionBlock>
          ) }
          <ProducesMetricsBlock metrics={ config.produces_metrics } />
        </>
      );
    } )
    .with( { prompt: P.string }, ( configuration ) => {
      const promptParams = Object.entries( configuration.prompt_parameters ?? {} );
      return (
        <>
          <SectionBlock title="Grading Prompt" data-testid="grading-prompt-block">
            <p className={ styles.body }>{ configuration.prompt }</p>
          </SectionBlock>
          { promptParams.length > 0 && (
            <SectionBlock title="Prompt Parameters" data-testid="prompt-parameters-block">
              <KeyValueTable entries={ promptParams } data-testid="prompt-parameters-table" />
            </SectionBlock>
          ) }
          <ProducesMetricsBlock metrics={ config.produces_metrics } />
        </>
      );
    } )
    .otherwise( () => (
      <SectionBlock title="Configuration" data-testid="unsupported-config-block">
        <p className={ styles.body }>Unsupported metric configuration.</p>
      </SectionBlock>
    ) );
}
